import { Component, Element, Fragment, Prop, Watch, getAssetPath, h } from '@stencil/core';

import { actions } from '../../actions/actions.dynamic-entry';
import { SelectOption } from '../../interfaces/interface.banner';
import { DuplicateOfferModal, VinModal } from '../../interfaces/interface.landing';
import { DynamicEntryInstallationMode, DynamicEntryStep, generateSeeValueLink } from '../../utils/utils.dynamic-entry';
import { DynamicEntryWorkflow } from '../../utils/utils.landing';

const CAR_ICON = 'assets/why-vin-icon.png';

@Component({
  tag: 'ico-dynamic-entry',
  styleUrl: 'dynamic-entry.css',
  assetsDirs: ['assets'],
})
export class DynamicEntry {
  @Element() el: HTMLElement;
  @Prop() dynamicEntryWorkflow: DynamicEntryWorkflow = DynamicEntryWorkflow.Unassigned;
  @Prop() enableMakeModel: boolean = true;
  @Prop() tabHeaders: [] = [];
  @Prop() currentSelectedTab: string = 'vin';
  @Prop() yearId: number = 0;
  @Prop() make: string = '';
  @Prop() model: string = '';
  @Prop() trim: string = '';
  @Prop() trimId: number = 0;
  @Prop() ymmSuggestions = [];
  @Prop() vin: string = '';
  @Prop() mileage: number = 0;
  @Prop() activeStep: DynamicEntryStep = DynamicEntryStep.FormSelector;
  @Prop() vehiclePossibilities: SelectOption[] = [];
  @Prop() hasMultipleTrims: boolean = false;
  @Prop() furyUrl: string = '';
  @Prop() inputVin: string = '';
  @Prop() licensePlateNumber: string = '';
  @Prop() licensePlateState: string = '';
  @Prop() inputYmm: string = '';
  @Prop() vehicleId: number = 0;
  @Prop() shouldOpenFunnelModal: boolean = false;
  @Prop() states: SelectOption[] = [];
  @Prop() vinError: string = '';
  @Prop() lpError: string = '';
  @Prop() styleError: string = '';
  @Prop() mileageError: string = '';
  @Prop() hasVinError: boolean = false;
  @Prop() hasLPError: boolean = false;
  @Prop() hasYmmError: boolean = false;
  @Prop() hasYmmOption2: boolean = false;
  @Prop() hasStyleError: boolean = false;
  @Prop() hasMileageError: boolean = false;
  @Prop() enableTermsConditions: boolean = false;
  @Prop() isFetching: boolean = false;
  @Prop() shouldShowIneligibleButton: boolean = true;
  @Prop() shouldShowStyleSelection: boolean = false;
  @Prop() sdContainer: string = '';
  @Prop() sdOemDealerCode: string = '';
  @Prop() dealerId: string = '';
  @Prop() isTabRendering: boolean = true;
  @Prop() isComponentRendering: boolean = true;

  @Prop() whenTermsConditionsClicked = () => {};

  @Prop() vinActiveDealerUrl: string = '';
  @Prop() hasVinActiveAutotraderListingError: boolean = false;
  @Prop() installationMode: DynamicEntryInstallationMode;
  @Prop() ymmDisabled: boolean = false;
  @Prop() vinModal: VinModal = {} as VinModal;
  @Prop() duplicateOfferModal: DuplicateOfferModal = {} as DuplicateOfferModal;

  funnelModal: HTMLIcoIframeModalElement;

  @Watch('shouldOpenFunnelModal')
  openFunnelModal(shouldOpen: boolean) {
    if (shouldOpen) {
      this.funnelModal.open();
    }
  }

  render() {
    const isFormSelectorStep = this.activeStep === DynamicEntryStep.FormSelector;
    const isMileageStep = [DynamicEntryStep.MSM, DynamicEntryStep.ThankYou].includes(this.activeStep);
    const isNotQualifiedStep = this.activeStep === DynamicEntryStep.NotQualify;
    const showTermsAndConditions = !this.isFetching && this.enableTermsConditions;
    const showLoadingSkeleton = this.isComponentRendering && isFormSelectorStep;

    return (
      <Fragment>
        {!!this.sdContainer && <ico-analytics-shift-digital container={this.sdContainer} oemDealerCode={this.sdOemDealerCode} />}

        {this.installationMode === DynamicEntryInstallationMode.PopUp && <ico-iframe-modal url={this.furyUrl} ref={(el) => (this.funnelModal = el)} />}

        <dynamic-skeleton-loading-container class={{ block: showLoadingSkeleton, hidden: !showLoadingSkeleton }} />
        <div
          class={{
            'max-w-540': true,
            block: !showLoadingSkeleton,
            hidden: showLoadingSkeleton,
          }}>
          <div class="dynamic-entry">
            <dynamic-entry-car-info
              vin={this.vin}
              year={this.yearId}
              make={this.make}
              model={this.model}
              trim={this.trim}
              currentSelectedTab={this.currentSelectedTab}
              hasMultipleTrims={this.hasMultipleTrims}
              isFormSelectorStep={isFormSelectorStep}
              dynamicEntryWorkflow={this.dynamicEntryWorkflow}
            />

            {isFormSelectorStep && (
              <div class="flex flex-col justify-between h-full">
                <dynamic-form-selector
                  tabs={this.tabHeaders}
                  selectedTab={this.currentSelectedTab}
                  changeTab={actions.onTabSelected}
                  isTabRendering={this.isTabRendering}
                  componentPrefix="dynamic-"
                  componentSuffix="-entry"
                  useSkeletonLoading
                  tabAttributes={{
                    vin: {
                      vin: this.inputVin,
                      enableMakeModel: this.enableMakeModel,
                      hasError: this.hasVinError,
                      errorMessage: this.vinError,
                      isFetching: this.isFetching,
                    },
                    plate: {
                      states: this.states,
                      licensePlateNumber: this.licensePlateNumber,
                      licensePlateState: this.licensePlateState,
                      enableMakeModel: this.enableMakeModel,
                      hasError: this.hasLPError,
                      errorMessage: this.lpError,
                      isFetching: this.isFetching,
                    },
                    ymm: {
                      ymmSuggestions: this.ymmSuggestions,
                      vehicleId: this.vehicleId,
                      value: this.inputYmm,
                      hasError: this.hasYmmError,
                      isFetching: this.isFetching,
                      isDisabled: this.ymmDisabled,
                    },
                  }}
                />
                <div class="flex mt-3 text-xxs">
                  <div class="flex content-center mr-2">
                    <img src={getAssetPath(CAR_ICON)} alt="why-vin" class="h-10 w-10" />
                  </div>
                  <div class="flex-auto">
                    <span class="font-bold block">Why Enter My VIN?</span>
                    <span class="block">Unlock your car's style, equipment and more, by simply entering your VIN or License Plate.</span>
                  </div>
                </div>
                {this.hasYmmOption2 && (
                  <div>
                    <hr class="mt-5 mb-5"></hr>
                    <dynamic-ymm-entry
                      isPrimaryInput={false}
                      ymmSuggestions={this.ymmSuggestions}
                      value={this.inputYmm}
                      vehicleId={this.vehicleId}
                      hasError={this.hasYmmError}
                      isDisabled={this.ymmDisabled}
                    />
                  </div>
                )}
              </div>
            )}

            {isMileageStep && (
              <Fragment>
                {this.hasVinActiveAutotraderListingError && (
                  <dynamic-entry-active-listing-error
                    model={this.model}
                    trim={this.trim}
                    vin={this.vin}
                    yearId={this.yearId}
                    make={this.make}
                    errorMessage={this.vinError}
                    changeVehicle={actions.onVehicleChanged}
                    vinActiveDealerUrl={this.vinActiveDealerUrl}
                    hasVinActiveAutotraderListingError={true}
                  />
                )}
                {!this.hasVinActiveAutotraderListingError && (
                  <dynamic-entry-model-style-mileage
                    dynamicEntryWorkflow={this.dynamicEntryWorkflow}
                    vehiclePossibilities={this.vehiclePossibilities}
                    yearId={this.yearId}
                    make={this.make}
                    model={this.model}
                    vin={this.vin}
                    trimId={this.trimId}
                    trim={this.trim}
                    mileage={this.mileage}
                    showStyle={this.shouldShowStyleSelection}
                    hasStyleError={this.hasStyleError}
                    hasMileageError={this.hasMileageError}
                    errorMessage={this.styleError || this.mileageError}
                    changeVehicle={actions.onVehicleChanged}
                    shouldShowIneligibleButton={this.shouldShowIneligibleButton}
                    isFetching={this.isFetching}
                    dealer-id={this.dealerId}
                    whenClickNext={() => this.shouldOpenFunnelModal && this.funnelModal.open()}
                  />
                )}
              </Fragment>
            )}

            {isNotQualifiedStep && (
              <dynamic-entry-ineligible
                dynamicEntryWorkflow={this.dynamicEntryWorkflow}
                vin={this.vin}
                seeValuesLink={generateSeeValueLink({ yearId: this.yearId, make: this.make, model: this.model, trim: this.trim, trimId: this.trimId })}
                changeVehicle={actions.onVehicleChanged}
                shouldShowIneligibleButton={this.shouldShowIneligibleButton}
              />
            )}
          </div>
          {showTermsAndConditions && (
            <div data-testid="ci-legal-text" class="mt-1 pl-1 text-xxs font-open-sans font-bold">
              <span>
                *For limitations, see the Kelley Blue Book&nbsp;
                <ico-hyperlink
                  testId="bi-btn-term-and-contitions"
                  class="underline cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    this.whenTermsConditionsClicked();
                  }}>
                  Program Terms and Conditions
                </ico-hyperlink>
              </span>
            </div>
          )}
        </div>
        {this.vinModal.open && <dynamic-entry-vin-modal vin={this.vin} mismatched={this.vinModal.mismatch} hasVinOrLPError={this.hasVinError || this.hasLPError} />}
        {this.duplicateOfferModal.open && !this.vinModal.open && <dynamic-entry-duplicate-offer-modal {...this.duplicateOfferModal} />}
      </Fragment>
    );
  }
}
